/**
 * PWAInstallBanner.js – iOS-Anleitung + Android Ein-Klick-Install
 *
 * Android (Chrome):  beforeinstallprompt → Ein-Klick-Button
 * iOS (Safari/WebKit): kein beforeinstallprompt → manuelle Schritt-Anleitung
 *                       "Teilen → Zum Home-Bildschirm"
 *
 * Beide:
 * - Dismissed-State in localStorage (erscheint nicht erneut)
 * - Bereits-installiert-Erkennung (display-mode: standalone / navigator.standalone)
 */

import React, { useState, useEffect } from 'react';
import { Download, X, Share, MoreHorizontal } from 'lucide-react';

const DISMISSED_KEY = 'pwa_install_dismissed_v2';

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  // WebKit ohne Chrome-Kennung = Safari (inkl. alle iOS-Browser da iOS-WebKit erzwingt)
  const isSafariWebKit = isIOS && !/CriOS|FxiOS|OPiOS|EdgiOS/i.test(ua);
  return { isIOS, isAndroid, isSafariWebKit };
}

function isAlreadyInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export const PWAInstallBanner = () => {
  const [installPrompt, setInstallPrompt] = useState(null); // Android
  const [showIOS, setShowIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isAlreadyInstalled() || localStorage.getItem(DISMISSED_KEY)) return;

    const { isIOS, isSafariWebKit } = detectPlatform();

    if (isIOS && isSafariWebKit) {
      // iOS Safari: nach 1,5 s einblenden (Page vollständig geladen)
      const t = setTimeout(() => { setShowIOS(true); setVisible(true); }, 1500);
      return () => clearTimeout(t);
    }

    // Android / Desktop Chrome: beforeinstallprompt abfangen
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
      setInstallPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  /* ── iOS-Banner ──────────────────────────────────────────────────────── */
  if (showIOS) {
    return (
      <div
        data-testid="pwa-install-banner-ios"
        className="fixed bottom-0 left-0 right-0 z-50 px-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="bg-card border-t border-border shadow-2xl px-4 pt-4 pb-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl overflow-hidden bg-[#092952] flex-shrink-0">
                <img src="/icon-192x192.png" alt="AllFlowX" className="h-full w-full object-cover" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground leading-tight">AllFlowX installieren</p>
                <p className="text-xs text-muted-foreground mt-0.5">Schneller Zugriff vom Home-Bildschirm</p>
              </div>
            </div>
            <button
              data-testid="pwa-dismiss-btn"
              onClick={handleDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors p-1 -mr-1 -mt-1"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Schritt-für-Schritt-Anleitung */}
          <div className="flex items-center gap-0 text-xs">
            {/* Schritt 1 */}
            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="h-10 w-10 rounded-2xl bg-blue-600 flex items-center justify-center shadow-md">
                {/* iOS Teilen-Symbol */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                  <polyline points="16 6 12 2 8 6"/>
                  <line x1="12" y1="2" x2="12" y2="15"/>
                </svg>
              </div>
              <p className="text-center text-muted-foreground leading-tight text-[10px]">
                Tippe auf<br/><span className="font-semibold text-foreground">„Teilen"</span>
              </p>
            </div>

            {/* Pfeil */}
            <div className="text-muted-foreground/50 text-base mx-1">›</div>

            {/* Schritt 2 */}
            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="h-10 w-10 rounded-2xl bg-muted border border-border flex items-center justify-center">
                {/* Plus in Quadrat (iOS "Zum Home-Bildschirm"-Symbol) */}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-foreground">
                  <rect x="3" y="3" width="18" height="18" rx="4"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
              </div>
              <p className="text-center text-muted-foreground leading-tight text-[10px]">
                Wähle<br/><span className="font-semibold text-foreground">„Zum Home-<br/>Bildschirm"</span>
              </p>
            </div>

            {/* Pfeil */}
            <div className="text-muted-foreground/50 text-base mx-1">›</div>

            {/* Schritt 3 */}
            <div className="flex flex-col items-center gap-1.5 flex-1">
              <div className="h-10 w-10 rounded-2xl bg-[#092952] overflow-hidden shadow-md">
                <img src="/icon-192x192.png" alt="AllFlowX" className="h-full w-full object-cover" />
              </div>
              <p className="text-center text-muted-foreground leading-tight text-[10px]">
                <span className="font-semibold text-foreground">Fertig!</span><br/>App gestartet
              </p>
            </div>
          </div>

          {/* Pfeil nach unten zur Safari-Leiste */}
          <div className="flex justify-center mt-3">
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17l-5-5h10l-5 5zM12 7v8"/>
              </svg>
              Das Teilen-Symbol findest du in der Safari-Menüleiste unten
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── Android-Banner (Ein-Klick-Install) ──────────────────────────────── */
  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] left-4 right-4 z-50 sm:left-auto sm:right-6 sm:w-80"
    >
      <div className="bg-card border border-border rounded-2xl shadow-xl p-4 flex items-center gap-3">
        <div className="flex-shrink-0 h-11 w-11 rounded-xl overflow-hidden bg-[#092952]">
          <img src="/icon-192x192.png" alt="AllFlowX" className="h-full w-full object-cover" />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-tight">
            AllFlowX installieren
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Zum Home-Bildschirm hinzufügen
          </p>
        </div>

        <button
          data-testid="pwa-install-btn"
          onClick={handleAndroidInstall}
          className="flex-shrink-0 flex items-center gap-1.5 bg-[#094199] hover:bg-[#0a3580] text-white text-xs font-semibold px-3 py-2 rounded-xl transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          App
        </button>

        <button
          data-testid="pwa-dismiss-btn"
          onClick={handleDismiss}
          className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors -mr-1"
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
